import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { apiCall } from "../../helper/Common";
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from "../../config/env";
import { normalizeShopDomain } from "../../utils/shopDomain";
import {
  loadInstallSession,
  saveInstallSession,
  clearInstallSession,
} from "../../helper/installSessionStorage";

const rejectPayload = (message, redirecting = false) => ({ message, redirecting });

const getRejectMessage = (payload) => {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") return payload.message || "Install check failed";
  return "Install check failed";
};

const isRedirectReject = (payload) => {
  if (payload && typeof payload === "object" && payload.redirecting) return true;
  const msg = getRejectMessage(payload).toLowerCase();
  return (
    msg.includes("shop not installed") ||
    msg.includes("app not installed") ||
    msg.includes("app not a installed")
  );
};

const handleRedirect = (shop, host) => {
  if (!shop) {
    console.warn("[handleRedirect] Shop parameter missing, cannot redirect");
    return false;
  }

  const redirectUrl = buildNodeApiEntryUrl(shop, host);
  console.log("redirectUrl", redirectUrl);

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const appHost = host || urlParams.get("host");

    if (appHost && SHOPIFY_API_KEY) {
      try {
        const app =
          (typeof window !== "undefined" && window.__shopifyAppBridgeInstance) ||
          createApp({
            apiKey: SHOPIFY_API_KEY,
            host: appHost,
          });
        const redirect = Redirect.create(app);
        console.log("redirect", redirect);
        redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
        console.log("redirect.dispatch", redirect.dispatch);
        return true;
      } catch (appBridgeError) {
        console.warn("[handleRedirect] App Bridge redirect failed:", appBridgeError);
      }
    }
  } catch (redirectError) {
    console.warn("[handleRedirect] App Bridge redirect failed, trying fallback:", redirectError);
  }

  try {
    if (window.top && window.top !== window) {
      window.top.location.href = redirectUrl;
    } else {
      window.location.href = redirectUrl;
    }
    return true;
  } catch (navError) {
    console.error("[handleRedirect] Navigation failed, opening in new window:", navError);
    window.open(redirectUrl, "_blank");
    return true;
  }
};
const extractShopAndHost = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let shop = urlParams.get("shop");
  let host = urlParams.get("host");

  if (!shop) {
    // Shopify admin URL fallback: /store/{store-handle}/apps/{app-handle}
    const pathMatch = window.location.pathname.match(/\/store\/([^/]+)\/apps\/[^/]+/i);
    if (pathMatch && pathMatch[1]) {
      shop = `${decodeURIComponent(pathMatch[1])}.myshopify.com`;
    }
  }

  if (!shop && host) {
    // host is base64-encoded admin host in embedded apps; recover store handle when possible.
    try {
      const decodedHost = atob(host);
      const decodedMatch = decodedHost.match(/\/store\/([^/]+)/i);
      if (decodedMatch && decodedMatch[1]) {
        shop = `${decodeURIComponent(decodedMatch[1])}.myshopify.com`;
      }
    } catch (e) {
      // ignore invalid base64 host
    }
  }

  if (!shop) {
    const currentUrl = window.location.href;
    const shopMatch = currentUrl.match(/[?&]shop=([^&]+)/);
    if (shopMatch) {
      shop = decodeURIComponent(shopMatch[1]);
    }
    const hostMatch = currentUrl.match(/[?&]host=([^&]+)/);
    if (hostMatch && !host) {
      host = decodeURIComponent(hostMatch[1]);
    }
  }

  return {
    shop: shop ? normalizeShopDomain(shop) : shop,
    host,
  };
};

function shopsMatch(a, b) {
  if (!a || !b) return false;
  return normalizeShopDomain(a) === normalizeShopDomain(b);
}

const initialInstallState = {
  checked: false,
  success: false,
  loading: false,
  redirecting: false,
  error: null,
  shop: null,
  host: null,
  installData: null,
};

export const checkInstall = createAsyncThunk(
  "install/checkInstall",
  async (arg, { rejectWithValue }) => {
    const { shop, host } = extractShopAndHost();

    if (!shop) {
      return rejectWithValue(
        rejectPayload(
          "Shop parameter is missing. Please access the app from Shopify admin.",
          false,
        ),
      );
    }

    try {
      const response = await apiCall("install", "POST", { shop }, null, { timeout: 8000 });

      if (response?.success || response?.message === "success" || response?.data) {
        return {
          success: true,
          shop: shop,
          host: host,
          data: response.data || response,
          message: response.message || "Install check successful",
        };
      }

      const redirecting = handleRedirect(shop, host);
      if (redirecting) {
        clearInstallSession();
      }
      return rejectWithValue(
        rejectPayload(response?.message || "Install API call failed", redirecting),
      );
    } catch (error) {
      let errorMessage = "App not installed";
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      const redirecting = handleRedirect(shop, host);
      if (redirecting) {
        clearInstallSession();
      }

      return rejectWithValue(rejectPayload(errorMessage, redirecting));
    }
  },
  {
    /** No second POST /install on route change — only first load or force refresh. */
    condition: (arg, { getState }) => {
      const forceRefresh =
        arg === true || (arg && typeof arg === "object" && arg.force === true);
      if (forceRefresh) return true;
      const state = getState().install;
      const { shop } = extractShopAndHost();
      if (!shop) return true;
      if (state.checked && state.success && state.installData && shopsMatch(state.shop, shop)) {
        return false;
      }
      return true;
    },
  },
);

const installSlice = createSlice({
  name: "install",
  initialState: initialInstallState,
  reducers: {
    hydrateInstallFromSession: (state) => {
      const session = loadInstallSession();
      if (!session?.installData || !session.shop) return;
      state.checked = true;
      state.success = true;
      state.loading = false;
      state.redirecting = false;
      state.error = null;
      state.shop = normalizeShopDomain(session.shop);
      state.host = session.host ?? null;
      state.installData = session.installData;
    },
    resetInstall: (state) => {
      clearInstallSession();
      state.checked = false;
      state.success = false;
      state.loading = false;
      state.redirecting = false;
      state.error = null;
      state.shop = null;
      state.host = null;
      state.installData = null;
    },
    setShopAndHost: (state, action) => {
      state.shop = action.payload.shop;
      state.host = action.payload.host;
    },
    /** Merge clients.is_on_boarding after POST /api/client/onboarding */
    updateInstallOnboarding: (state, action) => {
      const v = String(action.payload ?? "0");
      if (state.installData?.shop_info) {
        state.installData.shop_info.is_on_boarding = v;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkInstall.pending, (state) => {
        if (!state.installData) {
          state.loading = true;
        }
        state.redirecting = false;
        state.error = null;
      })
      .addCase(checkInstall.fulfilled, (state, action) => {
        state.loading = false;
        state.redirecting = false;
        state.checked = true;
        state.success = true;
        state.shop = normalizeShopDomain(action.payload.shop);
        state.host = action.payload.host;
        state.installData = action.payload.data;
        state.error = null;
        saveInstallSession({
          shop: state.shop,
          host: state.host,
          installData: state.installData,
        });
      })
      .addCase(checkInstall.rejected, (state, action) => {
        const redirecting = isRedirectReject(action.payload);
        state.redirecting = redirecting;
        state.loading = redirecting;
        state.checked = !redirecting;
        state.success = false;
        state.error = redirecting ? null : getRejectMessage(action.payload);

        const { shop, host } = extractShopAndHost();
        state.shop = state.shop || shop;
        state.host = state.host || host;
      });
  },
});

export const { resetInstall, setShopAndHost, updateInstallOnboarding, hydrateInstallFromSession } =
  installSlice.actions;

export const selectClientId = (state) => {
  return state.install.installData?.shop_info?.id ?? null;
};

export default installSlice.reducer;
